import {
  PANTHER_MANAGED_LABEL,
  FieldFilterTypes,
} from 'Components/forms/DetectionFieldFiltersForm/constants';
import React from 'react';
import { BoxProps } from 'pounceV2';
import CompoundInputV3, { AutoCompleteInputProps } from 'Components/CompoundInput/CompoundInputV3';
import {
  useGetFlatFilterFields,
  GetFlatFilterFields,
} from 'Source/graphql/queries/getFlatFilterFields.generated';

export type FieldFilterProp<T = GetFlatFilterFields['flatFilterFields']['flatFields'][number]> = {
  logTypes?: Array<string>;
  allowJSONPath?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  color?: string;
} & Partial<AutoCompleteInputProps<T>> &
  BoxProps;

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression for displayName
const FilterFieldCombobox = React.forwardRef(function FilterFieldCombobox<T>(
  props: FieldFilterProp<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
): React.ReactElement {
  const {
    logTypes = [],
    allowJSONPath = false,
    label = 'Field',
    placeholder = 'Select a field',
    disabled,
    color = 'blue-200',
    items = [],
    menuTitle,
    itemToString,
    itemToGroup,
    ...fieldProps
  } = props;

  const { loading, data: { flatFilterFields: { flatFields } = {} } = {} } = useGetFlatFilterFields({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        logTypes,
        showUnsafeTypes: true,
      },
    },
    skip: Boolean(items.length) || !logTypes.length,
  });

  const filterFields = React.useMemo(
    () =>
      items.length
        ? items
        : ((flatFields || []).filter(
            ({ type = '' }) =>
              FieldFilterTypes[type.toUpperCase()] &&
              (allowJSONPath ||
                ![FieldFilterTypes.JSON, FieldFilterTypes.JSON_ARRAY].includes(
                  FieldFilterTypes[type.toUpperCase()],
                )),
          ) as T[]),
    [items, flatFields, allowJSONPath],
  );

  const handleItemToString = React.useCallback(
    (item: T): string => {
      if (itemToString) {
        return itemToString(item);
      }
      if (items.length) {
        return String(item);
      }
      return (item as GetFlatFilterFields['flatFilterFields']['flatFields'][number]).target;
    },
    [itemToString, items.length],
  );

  const handleItemToGroup = React.useCallback(
    (item: T): string => {
      if (itemToGroup) {
        return itemToGroup(item);
      }
      if (items.length) {
        return String(item);
      }
      return (item as GetFlatFilterFields['flatFilterFields']['flatFields'][number])
        .isPantherManaged
        ? PANTHER_MANAGED_LABEL
        : (item as GetFlatFilterFields['flatFilterFields']['flatFields'][number]).logType;
    },
    [itemToGroup, items.length],
  );

  return (
    <CompoundInputV3.AutoCompleteInput<T>
      fuzzySearch
      allowFreeInput
      menuTitle={menuTitle}
      label={label}
      placeholder={placeholder}
      disabled={disabled || loading}
      color={color}
      items={filterFields}
      itemToString={handleItemToString}
      itemToGroup={handleItemToGroup}
      {...fieldProps}
      ref={ref}
    />
  );
});

export default FilterFieldCombobox;
